// src/pages/Dashboard/Admin/Payments/components/PaymentSpecialDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { FaPlus, FaSave, FaTimes, FaTrash } from "react-icons/fa";

import { getAllUsers } from "../../../../../services/userServices";
import { getArticles } from "../../../../../services/articlesServices";
import { createSpecialAchat } from "../../../../../services/achatsServices";
import { getSettings } from "../../../../../services/settingsServices";
import { sendNotification } from "../../../../../services/notificationServices";

import {
  formatEuro,
  safeArray,
  toAmount,
} from "../utils/paymentsDashboardUtils";

function DrawerField({ label, children, hint }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      {children}

      {hint ? (
        <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderRadius: 16,
    borderColor: state.isFocused ? "#818cf8" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(129, 140, 248, .45)" : "none",
    backgroundColor: "transparent",
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: 12,
    paddingRight: 8,
  }),
};

function getPreviousSeason(currentSeason) {
  const [startYear, endYear] = String(currentSeason).split("-").map(Number);
  if (!startYear || !endYear) return "";
  return `${startYear - 1}-${endYear - 1}`;
}

function getNextSeason(currentSeason) {
  const [startYear, endYear] = String(currentSeason).split("-").map(Number);
  if (!startYear || !endYear) return "";
  return `${startYear + 1}-${endYear + 1}`;
}

function isReductionArticleName(name = "") {
  return (
    /reduction_foyer_\d+/i.test(name) ||
    /-?\d+(?:[.,]\d+)?\s*%/.test(name) ||
    /réduction|remise|discount/i.test(name)
  );
}

function extractReductionPercent(name = "") {
  const byKey = name.match(/reduction_foyer_(\d+)/i);
  if (byKey) return Number.parseFloat(byKey[1]);

  const byPercent = name.match(/(-?\d+(?:[.,]\d+)?)\s*%/);
  if (byPercent) {
    return Math.abs(Number.parseFloat(String(byPercent[1]).replace(",", ".")));
  }

  return null;
}

function roundEuro(value) {
  return Math.round(value);
}

export default function PaymentSpecialDrawer({ isOpen, onClose, onSaved }) {
  const dialogRef = useRef(null);

  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [defaultPhotoUrl, setDefaultPhotoUrl] = useState("");

  const [selectedUser, setSelectedUser] = useState("");
  const [season, setSeason] = useState("");
  const [selectedArticles, setSelectedArticles] = useState([
    { article_id: "", quantite: "1" },
  ]);

  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [chosenDiscountArticleId, setChosenDiscountArticleId] = useState("");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const token = useMemo(() => localStorage.getItem("token") || "", []);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;

    async function loadData() {
      try {
        setLoading(true);

        const [usersData, articlesData, settingsData] = await Promise.all([
          getAllUsers(),
          getArticles(),
          getSettings(),
        ]);

        if (!mounted) return;

        const settings = Array.isArray(settingsData)
          ? settingsData[0] || {}
          : settingsData || {};

        const activeSeason = settings.saison_active || "";

        setUsers(safeArray(usersData));
        setArticles(safeArray(articlesData));
        setDefaultPhotoUrl(settings.photo_url_default_lic_cot || "");

        if (activeSeason) {
          setSeason(activeSeason);
          setSeasons(
            [
              getPreviousSeason(activeSeason),
              activeSeason,
              getNextSeason(activeSeason),
            ].filter(Boolean)
          );
        } else {
          setSeason("");
          setSeasons([]);
        }

        setTimeout(() => {
          dialogRef.current?.querySelector(".rs__control input")?.focus();
        }, 0);
      } catch (error) {
        console.error("Erreur chargement drawer licence :", error);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onEsc = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    return () => window.removeEventListener("keydown", onEsc);
  }, [isOpen, onClose, submitting]);

  const userOptions = useMemo(() => {
    return users.map((user) => {
      const adherentsLabel = user.adherents?.length
        ? ` (${user.adherents
            .map((adherent) =>
              `${adherent?.prenom || ""} ${adherent?.nom || ""}`.trim()
            )
            .filter(Boolean)
            .join(", ")})`
        : "";

      return {
        value: user.id,
        label: `${user.nom || ""} ${user.prenom || ""}${adherentsLabel}`.trim(),
      };
    });
  }, [users]);

  const articleOptions = useMemo(() => {
    return articles.map((article) => ({
      value: article.id,
      label: article.nom || "Article sans nom",
      price: toAmount(article.prix),
      image: article.photo_url || defaultPhotoUrl,
    }));
  }, [articles, defaultPhotoUrl]);

  const reductionCandidates = useMemo(() => {
    return articles
      .filter(
        (article) =>
          isReductionArticleName(article.nom || "") || toAmount(article.prix) < 0
      )
      .map((article) => ({
        value: article.id,
        label: `${article.nom} (${formatEuro(article.prix)})`,
      }));
  }, [articles]);

  const baseTotal = useMemo(() => {
    return selectedArticles.reduce((sum, row) => {
      const option = articleOptions.find(
        (item) => String(item.value) === String(row.article_id)
      );

      const quantity = Number(String(row.quantite).replace(",", ".")) || 0;

      return sum + (option?.price || 0) * quantity;
    }, 0);
  }, [selectedArticles, articleOptions]);

  const discountPreview = useMemo(() => {
    if (!applyDiscount) return 0;

    const value = Number(String(discountValue).replace(",", ".")) || 0;

    if (value <= 0) return 0;

    const totalWithoutReductions = selectedArticles.reduce((sum, row) => {
      const article = articles.find(
        (item) => String(item.id) === String(row.article_id)
      );

      if (!article) return sum;
      if (isReductionArticleName(article.nom || "")) return sum;

      const quantity = Number(String(row.quantite).replace(",", ".")) || 0;

      return sum + toAmount(article.prix) * quantity;
    }, 0);

    if (discountType === "percent") {
      return -roundEuro(Math.abs((totalWithoutReductions * value) / 100));
    }

    return -Math.abs(value);
  }, [
    applyDiscount,
    discountValue,
    discountType,
    selectedArticles,
    articles,
  ]);

  const estimatedTotal = baseTotal + discountPreview;

  const resetForm = () => {
    setSelectedUser("");
    setSelectedArticles([{ article_id: "", quantite: "1" }]);
    setApplyDiscount(false);
    setDiscountType("percent");
    setDiscountValue("");
    setChosenDiscountArticleId("");
  };

  const closeDrawer = () => {
    if (submitting) return;

    resetForm();
    onClose?.();
  };

  const addArticleRow = () => {
    setSelectedArticles((prev) => [...prev, { article_id: "", quantite: "1" }]);
  };

  const removeArticleRow = (index) => {
    setSelectedArticles((prev) =>
      prev.length === 1 ? prev : prev.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const updateArticleRow = (index, key, value) => {
    setSelectedArticles((prev) =>
      prev.map((row, itemIndex) =>
        itemIndex === index ? { ...row, [key]: value } : row
      )
    );
  };

  const changeQuantityBy = (index, delta) => {
    setSelectedArticles((prev) =>
      prev.map((row, itemIndex) => {
        if (itemIndex !== index) return row;

        const current = Number(String(row.quantite).replace(",", ".")) || 0;
        const next = Math.max(1, current + delta);

        return {
          ...row,
          quantite: String(next),
        };
      })
    );
  };

  const computeArticlesWithReductionPercentLines = (rows) => {
    const finalRows = [...rows];

    finalRows.forEach((line, index) => {
      const ref = articles.find(
        (article) => String(article.id) === String(line.article_id)
      );

      if (!ref) return;

      const percent = extractReductionPercent(ref.nom || "");
      if (!percent) return;

      const totalWithoutCurrentReduction = finalRows.reduce(
        (sum, currentRow, currentIndex) => {
          if (currentIndex === index) return sum;

          const currentRef = articles.find(
            (article) => String(article.id) === String(currentRow.article_id)
          );

          if (!currentRef) return sum;

          const unitPrice = toAmount(currentRef.prix);
          const quantity =
            Number(String(currentRow.quantite).replace(",", ".")) || 0;

          return sum + unitPrice * quantity;
        },
        0
      );

      const reductionValue = -roundEuro(
        Math.abs((totalWithoutCurrentReduction * percent) / 100)
      );

      const unitPrice = toAmount(ref.prix);
      const base = unitPrice !== 0 ? unitPrice : -1;
      const quantity = reductionValue / base;

      finalRows[index] = {
        ...line,
        quantite: String(Number(quantity.toFixed(2))),
      };
    });

    return finalRows;
  };

  const injectGlobalDiscountRow = (rows) => {
    if (!applyDiscount) return rows;

    const value = Number(String(discountValue).replace(",", ".")) || 0;

    if (value <= 0) return rows;

    const discountArticleId =
      chosenDiscountArticleId ||
      (reductionCandidates.length ? reductionCandidates[0].value : "");

    if (!discountArticleId) {
      toast.error(
        "Aucun article de réduction trouvé. Créez un article avec un prix négatif ou sélectionnez-en un."
      );
      throw new Error("No reduction article available");
    }

    const discountArticle = articles.find(
      (article) => String(article.id) === String(discountArticleId)
    );

    const unitPrice = toAmount(discountArticle?.prix);

    if (unitPrice === 0) {
      toast.error("L’article de réduction choisi a un prix unitaire nul.");
      throw new Error("Reduction article unit price is zero");
    }

    const totalWithoutReductions = rows.reduce((sum, row) => {
      const ref = articles.find(
        (article) => String(article.id) === String(row.article_id)
      );

      if (!ref) return sum;
      if (isReductionArticleName(ref.nom || "")) return sum;

      const quantity = Number(String(row.quantite).replace(",", ".")) || 0;

      return sum + toAmount(ref.prix) * quantity;
    }, 0);

    const discountAmount =
      discountType === "percent"
        ? -roundEuro(Math.abs((totalWithoutReductions * value) / 100))
        : -Math.abs(value);

    const quantity = discountAmount / unitPrice;

    const existingIndex = rows.findIndex(
      (row) => String(row.article_id) === String(discountArticleId)
    );

    if (existingIndex !== -1) {
      const next = [...rows];
      const existingQuantity =
        Number(String(next[existingIndex].quantite).replace(",", ".")) || 0;

      next[existingIndex] = {
        ...next[existingIndex],
        quantite: String(Number((existingQuantity + quantity).toFixed(2))),
      };

      return next;
    }

    return [
      ...rows,
      {
        article_id: String(discountArticleId),
        quantite: String(Number(quantity.toFixed(2))),
      },
    ];
  };

  const sendUserNotification = async (recipientId) => {
    const message =
      "Le responsable du club vous a affecté un nouveau règlement de licence-cotisation à régler sur la page Vos Commandes & Règlements.";

    await sendNotification(message, recipientId, token);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!selectedUser) {
      toast.warn("Sélectionnez un utilisateur.");
      return;
    }

    if (!season) {
      toast.warn("Sélectionnez une saison.");
      return;
    }

    const filledRows = selectedArticles.filter((row) => {
      const quantity = Number(String(row.quantite).replace(",", ".")) || 0;
      return row.article_id && quantity > 0;
    });

    if (!filledRows.length) {
      toast.warn("Ajoutez au moins un article.");
      return;
    }

    try {
      setSubmitting(true);

      let rows = computeArticlesWithReductionPercentLines(filledRows);
      rows = injectGlobalDiscountRow(rows);

      await createSpecialAchat({
        user_achat_id: selectedUser,
        saison: season,
        articles: rows.map((row) => ({
          article_id: row.article_id,
          quantite: Number(String(row.quantite).replace(",", ".")) || 1,
          photo_url:
            articleOptions.find(
              (option) => String(option.value) === String(row.article_id)
            )?.image || defaultPhotoUrl,
        })),
      });

      await sendUserNotification(selectedUser);

      toast.success("Règlement licence-cotisation ajouté avec succès.");

      await onSaved?.();

      resetForm();
      onClose?.();
    } catch (error) {
      console.error("Erreur création règlement licence :", error);

      if (error?.message !== "No reduction article available") {
        toast.error("Erreur lors de la création du règlement licence-cotisation.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={closeDrawer}
        aria-hidden
      />

      <aside
        ref={dialogRef}
        className="absolute right-0 top-0 flex h-full w-full max-w-4xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900"
      >
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Licence / cotisation
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Ajouter un règlement licence-cotisation-réduction
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Créez un règlement global pour une saison, composé de plusieurs
              articles : licence, assurance, cotisation, réduction foyer,
              remise ou acompte.
            </p>
          </div>

          <button
            type="button"
            onClick={closeDrawer}
            disabled={submitting}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          onKeyDown={(event) => {
            if (event.key === "Enter" && event.target.tagName !== "TEXTAREA") {
              event.preventDefault();
            }
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-4 lg:grid-cols-2">
              <DrawerField label="Utilisateur *">
                <Select
                  classNamePrefix="rs"
                  options={userOptions}
                  value={
                    userOptions.find(
                      (option) => String(option.value) === String(selectedUser)
                    ) || null
                  }
                  onChange={(option) => setSelectedUser(option?.value || "")}
                  placeholder="Sélectionnez un utilisateur..."
                  isClearable
                  isDisabled={loading || submitting}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </DrawerField>

              <DrawerField label="Saison *">
                <select
                  value={season}
                  onChange={(event) => setSeason(event.target.value)}
                  required
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                >
                  {seasons.length ? (
                    seasons.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))
                  ) : (
                    <option value="">Aucune saison active</option>
                  )}
                </select>
              </DrawerField>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Articles du règlement
                  </p>

                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Ajoutez les lignes nécessaires : licence, assurance,
                    cotisation, réduction ou article spécifique.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={addArticleRow}
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-bold text-indigo-700 transition hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
                >
                  <FaPlus />
                  Ajouter une ligne
                </button>
              </div>

              <div className="space-y-3">
                {selectedArticles.map((row, index) => {
                  const option =
                    articleOptions.find(
                      (item) => String(item.value) === String(row.article_id)
                    ) || null;

                  const quantity =
                    Number(String(row.quantite).replace(",", ".")) || 0;
                  const lineTotal = (option?.price || 0) * quantity;

                  return (
                    <div
                      key={`special-line-${index}`}
                      className="rounded-[22px] border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900"
                    >
                      <div className="grid gap-3 lg:grid-cols-[1fr_180px_120px_44px] lg:items-end">
                        <DrawerField label={`Article ${index + 1}`}>
                          <Select
                            classNamePrefix="rs"
                            options={articleOptions}
                            value={option}
                            onChange={(selected) =>
                              updateArticleRow(
                                index,
                                "article_id",
                                selected?.value || ""
                              )
                            }
                            placeholder="Choisir un article..."
                            isClearable
                            isDisabled={submitting}
                            menuPortalTarget={document.body}
                            styles={selectStyles}
                          />
                        </DrawerField>

                        <DrawerField label="Quantité">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => changeQuantityBy(index, -1)}
                              disabled={submitting}
                              className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200 text-lg font-bold text-slate-700 transition hover:bg-slate-300 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                            >
                              −
                            </button>

                            <input
                              type="text"
                              inputMode="decimal"
                              value={row.quantite}
                              onChange={(event) =>
                                updateArticleRow(
                                  index,
                                  "quantite",
                                  event.target.value
                                )
                              }
                              onBlur={() => {
                                const number =
                                  Number(
                                    String(row.quantite).replace(",", ".")
                                  ) || 1;

                                updateArticleRow(
                                  index,
                                  "quantite",
                                  String(Math.max(1, number))
                                );
                              }}
                              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-2 text-center text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                            />

                            <button
                              type="button"
                              onClick={() => changeQuantityBy(index, 1)}
                              disabled={submitting}
                              className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200 text-lg font-bold text-slate-700 transition hover:bg-slate-300 disabled:opacity-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                            >
                              +
                            </button>
                          </div>
                        </DrawerField>

                        <div>
                          <p className="mb-1 text-sm font-bold text-slate-700 dark:text-slate-200">
                            Total ligne
                          </p>

                          <div className="flex h-10 items-center justify-end rounded-xl border border-slate-200 bg-white px-3 text-sm font-bold text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white">
                            {formatEuro(lineTotal)}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeArticleRow(index)}
                          disabled={selectedArticles.length === 1 || submitting}
                          className="flex h-10 w-10 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                          title="Supprimer la ligne"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-bold text-slate-900 dark:text-white">
                    Réduction globale optionnelle
                  </p>

                  <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    Permet d’injecter automatiquement une ligne de réduction à
                    partir d’un article existant avec prix négatif.
                  </p>
                </div>

                <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={applyDiscount}
                    onChange={(event) => setApplyDiscount(event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                  />
                  Activer
                </label>
              </div>

              {applyDiscount ? (
                <div className="mt-4 grid gap-4 lg:grid-cols-3">
                  <DrawerField label="Type de remise">
                    <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700">
                      <button
                        type="button"
                        onClick={() => setDiscountType("percent")}
                        className={`px-3 py-2.5 text-sm font-bold transition ${
                          discountType === "percent"
                            ? "bg-[#013E8E] text-white dark:bg-indigo-500"
                            : "bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                        }`}
                      >
                        %
                      </button>

                      <button
                        type="button"
                        onClick={() => setDiscountType("fixed")}
                        className={`px-3 py-2.5 text-sm font-bold transition ${
                          discountType === "fixed"
                            ? "bg-[#013E8E] text-white dark:bg-indigo-500"
                            : "bg-white text-slate-700 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                        }`}
                      >
                        €
                      </button>
                    </div>
                  </DrawerField>

                  <DrawerField
                    label={
                      discountType === "percent"
                        ? "Valeur de remise (%)"
                        : "Montant de remise (€)"
                    }
                  >
                    <input
                      type="text"
                      inputMode="decimal"
                      value={discountValue}
                      onChange={(event) => setDiscountValue(event.target.value)}
                      placeholder={discountType === "percent" ? "Ex : 10" : "Ex : 15"}
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                    />
                  </DrawerField>

                  <DrawerField label="Article de réduction">
                    <Select
                      classNamePrefix="rs"
                      options={reductionCandidates}
                      value={
                        reductionCandidates.find(
                          (option) =>
                            String(option.value) ===
                            String(chosenDiscountArticleId)
                        ) || null
                      }
                      onChange={(option) =>
                        setChosenDiscountArticleId(option?.value || "")
                      }
                      placeholder="Réduction disponible..."
                      isClearable
                      menuPortalTarget={document.body}
                      styles={selectStyles}
                    />
                  </DrawerField>
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-[11px] uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                  Total brut
                </p>

                <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
                  {formatEuro(baseTotal)}
                </p>
              </div>

              <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/20 dark:bg-amber-500/10">
                <p className="text-[11px] uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
                  Réduction estimée
                </p>

                <p className="mt-2 text-2xl font-bold text-amber-700 dark:text-amber-300">
                  {formatEuro(discountPreview)}
                </p>
              </div>

              <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-500/20 dark:bg-emerald-500/10">
                <p className="text-[11px] uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
                  Total estimé
                </p>

                <p className="mt-2 text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                  {formatEuro(estimatedTotal)}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeDrawer}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={submitting || loading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
              >
                <FaSave />
                {submitting ? "Enregistrement..." : "Créer le règlement"}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>
  );
}